import React, { useCallback, useState, useRef, useMemo } from 'react';
import { Paper, Icon, IconButton, TextField, makeStyles, Menu, MenuItem, ListItemIcon, Button, Box, Theme } from '@material-ui/core';
import clsx from 'clsx';
import filterXSS from 'xss';
import { Comment, Issue } from '@moderepo/mode-apis';
import { useModePanelStyle, useModeFormStyle, FontIcon, useDropdown } from '@moderepo/mode-app-components';
import emojiShortcuts from '../../assets/emojis/emoji_shortcuts.json';



/**
 * Convert all ```message``` into multiline code block
 * @param message
 */
const multilineCodeBlockFormatter = (message: string): string => {
    return message.replace(/```([^```]*)```/g, '<code class="multiline"><pre>$1</pre></code>');
};


/**
 * Convert all `message` into single line code block
 * @param message
 */
const singleLineCodeBlockFormatter = (message: string): string => {
    return message.replace(/`([^`]*)`/g, '<code class="single-line">$1</code>');
};


const emojiFormatter = (message: string): string => {
    const customEmojis = {
        ':mario_luigi_dance:': require('../../assets/emojis/mario_luigi_dance.gif'),
    };

    // First try parsing default emojis tags e.g. convert :smiley: to "&#x1F603;"
    const defaultEmojisFormatResult = Object.keys(emojiShortcuts).reduce((result: string, emoji: string): string => {
        return result.replace(new RegExp(emoji, 'g'), `<span class="emoji" title="${emoji}">${emojiShortcuts[emoji]}</span>`);
    }, message);

    // Then convert custom emojis tags e.g. convert :mario_luigi_dance: to the mario_luigi_dance.gif image
    return Object.keys(customEmojis).reduce((result: string, emoji: string): string => {
        return result.replace(new RegExp(emoji, 'g'), `<img class="custom-emoji" title="${emoji}" src="${customEmojis[emoji]}"/>`);
    }, defaultEmojisFormatResult);
};



const useStyle = makeStyles((theme: Theme) => {
    return {
        panelContent: {
            background   : '#f0f0f0',
            display      : 'flex',
            flexDirection: 'column',
            padding      : 0,
        },

        emptyCommentsListMessage: {
            fontWeight: 500,
            color     : theme.palette.text.secondary,
            fontSize  : 'larger',
            padding   : theme.spacing(2),
            textAlign : 'center',
        },


        previousCommentsContainer: {
            display                     : 'flex',
            flexDirection               : 'column',
            width                       : '100%',
            flex                        : 1,
            overflow                    : 'hidden auto',
            padding                     : theme.spacing(1.5, 0),
            [theme.breakpoints.up('sm')]: {
                padding: theme.spacing(2, 0),
            },
        },


        commentGroupsContainer: {
            display      : 'flex',
            flexDirection: 'column',
        },

        commentGroup: {
            display: 'flex',
            margin : theme.spacing(2, 0),
            width  : '100%',

            '&.left': {
                flexDirection: 'row',
                alignSelf    : 'flex-start',

                '& $commentGroupCommentsContainer': {
                    borderTopLeftRadius: 0,
                    position           : 'relative',
                },
            },

            '&.right': {
                flexDirection: 'row-reverse',
                alignSelf    : 'flex-end',
                textAlign    : 'right',

                '& $commentGroupAvatarSection': {
                    marginRight: theme.spacing(0),
                    marginLeft : theme.spacing(1),
                },

                '& $commentGroupCommentsSection': {
                    alignItems: 'flex-end',
                },

                '& $commentGroupAgentInfo': {
                    flexDirection  : 'row-reverse',
                    '& .agent-name': {
                        marginRight: 0,
                        marginLeft : theme.spacing(1),
                    },
                },

                '& $commentGroupCommentsContainer': {
                    borderTopRightRadius: 0,
                },

                '& $comment': {
                    paddingRight: 0,
                    paddingLeft : theme.spacing(2),
                },

                '& $commentOptionToggler': {
                    right    : 'unset',
                    left     : 0,
                    transform: 'translate3D(-50%, -25%, 0)',
                },
            },
        },

        commentGroupAvatarSection: {
            marginRight: theme.spacing(1),
            display    : 'flex',
            alignItems : 'flex-start',
            '& .avatar': {
                color         : '#ffffff',
                width         : 40,
                height        : 40,
                borderRadius  : '50%',
                display       : 'flex',
                alignItems    : 'center',
                justifyContent: 'center',
                fontWeight    : 'bold',
                fontSize      : 14,
                '&.medium'    : {
                    fontSize: 18,
                },
                '&.large': {
                    fontSize: 24,
                },
                '&.system': {
                    background: theme.palette.primary.main,
                    color     : theme.palette.primary.contrastText,
                    fontSize  : 24,
                },
            },
        },


        commentGroupCommentsSection: {
            display      : 'flex',
            flexDirection: 'column',
            flex         : 1,
        },


        commentGroupAgentInfo: {
            marginTop      : theme.spacing(1),
            display        : 'flex',
            alignItems     : 'flex-start',
            flexDirection  : 'row',
            '& .agent-name': {
                fontWeight : 500,
                color      : theme.palette.text.primary,
                marginRight: 5,
                whiteSpace : 'nowrap',
            },

            '& .timestamp': {
                color: theme.palette.text.secondary,
            },
        },


        commentGroupCommentsContainer: {
            background   : theme.palette.background.paper,
            color        : theme.palette.text.primary,
            padding      : theme.spacing(2),
            borderRadius : 10,
            display      : 'flex',
            width        : '100%',
            flexDirection: 'column',
            border       : '1px solid rgba(0, 0, 0, 0.1)',
        },


        comment: {
            position            : 'relative',
            paddingRight        : theme.spacing(2),
            '&:not(:last-child)': {
                marginBottom: theme.spacing(2),
            },

            '&:hover, :active': {
                '& $commentOptionToggler': {
                    display: 'block',
                },
            },
        },

        commentOptionToggler: {
            display  : 'none',
            position : 'absolute',
            top      : 0,
            right    : 0,
            color    : theme.palette.primary.main,
            transform: 'translate3D(50%, -25%, 0)',

            '& .icon': {
                fontSize: 16,
            },
        },

        commentMessage: {
            padding: 0,
            margin : 0,
            color  : theme.palette.text.primary,

            '& code': {
                border      : '1px solid #cbb700',
                background  : '#fdfbe7',
                borderRadius: 4,
                padding     : '2px 5px',
                fontFamily  : 'monospace',
                fontSize    : 'larger',

                '&.single-line': {
                    display: 'inline',
                    margin : '0 2px',
                },

                '&.multiline': {
                    display : 'block',
                    margin  : '10px 0px',
                    padding : 10,
                    overflow: 'auto',

                    '& pre': {
                        margin     : 0,
                        display    : 'table',
                        tableLayout: 'fixed',
                        width      : '100%',
                    },
                },
            },

            '& .emoji': {
                display   : 'inline-block',
                fontSize  : 32,
                lineHeight: '32px',
            },

            '& .custom-emoji': {
                width        : 32,
                height       : 32,
                verticalAlign: 'bottom',
            },
        },

        commentInfo: {
            color   : theme.palette.text.secondary,
            fontSize: 'smaller',
        },


        commentMessageForm: {
            height                      : 'auto',
            padding                     : theme.spacing(1.5, 0),
            marginTop                   : theme.spacing(2),
            [theme.breakpoints.up('sm')]: {
                padding: theme.spacing(2, 0),
            },
        },


        commentMessageInput: {
            flex                  : 1,
            '& .MuiInputBase-root': {
                background: theme.palette.background.paper,
            },
        },

        commentMessageInputField: {
            background: '#ffffff',
        },

        optionsMenu: {
            '& .icon': {
                width   : 20,
                fontSize: 20,
                color   : theme.palette.primary.main,

                '&.delete-comment': {
                    color: theme.palette.error.main,
                },
            },
        },
    };
});



/**
 * A predefined set of colors used for the agent avatar's background. We will use the agent's ID as a hash to pick a color. This will guarantee
 * the same agent will get the same background color every time.
 */
const AGENT_AVATAR_COLOR: readonly string[] = [
    'Blue', 'Brown', 'Violet', 'Chocolate', 'DarkGreen', 'DarkMagenta', 'DarkRed', 'DarkSlateBlue', 'DarkOrchid', 'CornflowerBlue', 'DarkOrange',
    'FireBrick', 'ForestGreen', 'Green', 'Indigo', 'LightSeaGreen', 'Maroon', 'MediumPurple', 'MediumSeaGreen', 'Olive', 'OrangeRed', 'Red', 'Purple',
];


interface RCIssueCommentsProps {
    readonly className?: string;
    readonly title: string;
    readonly icon: string;
    readonly labels: {
        readonly you: string;
        readonly addCommentInputLabel: string;
        readonly addCommentPlaceholder: string;
        readonly addCommentHintText: string;
        readonly emptyCommentsListMessage: string;
        readonly editCommentInputLabel: string;
        readonly deleteCommentButtonLabel: string;
        readonly reloadCommentsButtonLabel: string;
        readonly updateButtonLabel: string;
        readonly cancelButtonLabel: string;
    }
    readonly issue: Issue;
    readonly commentGroups: readonly CommentGroup[];
    readonly onAddCommentSubmit: (message: string)=> Promise<boolean>;
    readonly onEditCommentSubmit: (comment: Comment, newMessage: string)=> Promise<boolean>;
    readonly onDeleteComment: (comment: Comment)=> void;
    readonly onReloadCommentsClick: ()=> void;
}


/**
 * We need to localize the comment's create date so we need to extend the Comment interface and add localizedCreatedDate
 */
export interface LocalizedComment extends Comment {
    readonly localizedCreatedDate: string;
}


/**
 * Comments are put into groups. All CONSECUTIVE comments from the same agent will be grouped into 1 group so that when we display them, we don't
 * need to show the agent's name and avatar multiple times.
 */
export interface CommentGroup {
    readonly id: string;
    readonly startDate: string;
    readonly agentId: number;
    readonly agentName: string;
    readonly agentInitial: string;              // 3 letters initial of the agent's name
    readonly agentAvatar?: JSX.Element;
    readonly isLoggedInAgentComments: boolean;  // whether this group of comments belonged to the logged in agent
    readonly comments: LocalizedComment[];
}


/**
 * This is a DUMB component used for displaying issue comments. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCIssueComments: React.FC<RCIssueCommentsProps> = (props: RCIssueCommentsProps) => {
    const [menuAnchorEl, isMenuOpened, onAnchorElClicked, onMenuClosed] = useDropdown();
    const [commentMessage, setCommentMessage] = useState<string>();
    const [selectedComment, setSelectedComment] = useState<Comment>();
    const [editingComment, setEditingComment] = useState<Comment>();
    const commentMessageInputRef = useRef<HTMLInputElement>();


    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();



    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (commentMessage) {
            if (editingComment) {
                // If we are in Edit comment mode, dispatch onEditCommentSubmit event
                props.onEditCommentSubmit(editingComment, commentMessage).then((success: boolean) => {
                    if (success) {
                        setCommentMessage('');
                        setEditingComment(undefined);
                        setSelectedComment(undefined);
                    }
                });
            } else {
                // Else, dispatch onAddCommentSubmit event
                props.onAddCommentSubmit(commentMessage).then((success: boolean) => {
                    if (success) {
                        setCommentMessage('');
                    }
                });
            }
        }

        return false;
    }, [props, editingComment, commentMessage]);



    /**
     * On Edit Comment menu item clicked
     */
    const onEditCommentClicked = useCallback(() => {
        if (selectedComment) {
            setCommentMessage(selectedComment.message);
            setEditingComment(selectedComment);

            if (commentMessageInputRef !== undefined && commentMessageInputRef.current !== undefined) {
                commentMessageInputRef.current.focus();
            }
        }
        setSelectedComment(undefined);
    }, [selectedComment]);



    /**
     * On Cancel edit comment button clicked.
     */
    const onCancelUpdateCommentClicked = useCallback(() => {
        setCommentMessage('');
        setEditingComment(undefined);
        if (commentMessageInputRef !== undefined && commentMessageInputRef.current !== undefined) {
            commentMessageInputRef.current.focus();
        }
        setSelectedComment(undefined);
    }, []);



    /**
     * On Delete Comment menu item clicked.
     */
    const onDeleteCommentClicked = useCallback(() => {
        if (selectedComment) {
            props.onDeleteComment(selectedComment);
            setSelectedComment(undefined);
        }
    }, [selectedComment, props]);



    /**
     * Given the group's agentId and group index, return whether the group should be displayed on the left or right side.
     * First, if we have already display a comments for this agent before, then we need to show this group of comments for the same agent on the
     * same side. This will make it easier to find the comments belonged to the same agent. If this is the first time we see comments for an agent
     * then we use the index to determine the side. If index is even number, show it on the left otherwise, show it on the right.
     */
    const messageDisplayLocationByAgentId: {[issueId: string]: {[agentId: string]: string}} = useMemo(() => {
        return {
            [props.issue.id]: {},
        };
    }, [props.issue]);

    const getGroupDisplayLocation = useCallback((agentId: number, index: number): string => {
        if (!messageDisplayLocationByAgentId[props.issue.id][agentId]) {
            messageDisplayLocationByAgentId[props.issue.id][agentId] = (index % 2 === 0 ? 'left' : 'left');
        }

        return messageDisplayLocationByAgentId[props.issue.id][agentId];
    }, [props.issue, messageDisplayLocationByAgentId]);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <div className={clsx(panelClasses.panelHeader)}>
                <div className={clsx(panelClasses.panelHeaderIconContainer)}>
                    <FontIcon className={clsx(panelClasses.panelHeaderIcon)} iconName={props.icon} />
                </div>
                <div className={panelClasses.panelHeaderTitle}>
                    {props.title}
                </div>
            </div>
            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>

                {props.commentGroups.length > 0 && (
                    <div className={classes.previousCommentsContainer}>
                        <div className={classes.commentGroupsContainer}>
                            {props.commentGroups.map((group: CommentGroup, groupIndex: number) => {
                                return (
                                    <div
                                        key={group.id}
                                        className={clsx(classes.commentGroup, getGroupDisplayLocation(group.agentId, groupIndex))}
                                    >
                                        <div className={clsx(classes.commentGroupAvatarSection)}>
                                            {group.agentAvatar ? (
                                                <div className={clsx('avatar', 'system')}>
                                                    {group.agentAvatar}
                                                </div>
                                            ) : (
                                                <div
                                                    className={clsx('avatar', group.agentInitial.length === 2 && 'medium',
                                                        group.agentInitial.length === 1 && 'large')}
                                                    style={{ background: AGENT_AVATAR_COLOR[group.agentId % AGENT_AVATAR_COLOR.length] }}
                                                >
                                                    {group.agentInitial}
                                                </div>
                                            )}
                                        </div>
                                        <div className={clsx(classes.commentGroupCommentsSection)}>
                                            <div
                                                className={clsx(classes.commentGroupCommentsContainer)}
                                            >
                                                {group.comments.map((comment: LocalizedComment) => {
                                                    return (
                                                        <div
                                                            key={comment.createdAt}
                                                            className={clsx(classes.comment)}
                                                        >
                                                            {!editingComment && group.isLoggedInAgentComments && (
                                                                <IconButton
                                                                    className={classes.commentOptionToggler}
                                                                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        onAnchorElClicked(event);
                                                                        setSelectedComment(comment);
                                                                    }}
                                                                >
                                                                    <Icon className="icon">more_vert</Icon>
                                                                </IconButton>
                                                            )}

                                                            <div
                                                                className={classes.commentMessage}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: emojiFormatter(singleLineCodeBlockFormatter(
                                                                        multilineCodeBlockFormatter(
                                                                            filterXSS(comment.message),
                                                                        ),
                                                                    )),
                                                                }}
                                                            />


                                                            {/*
                                                                If there are more than 1 comments in the group, show the timestamp for each comment
                                                            */}
                                                            {group.comments.length > 1 && (
                                                                <div className={classes.commentInfo}>
                                                                    {comment.localizedCreatedDate}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className={clsx(classes.commentGroupAgentInfo)}>
                                                <div className="agent-name">
                                                    {group.isLoggedInAgentComments ? props.labels.you : group.agentName}
                                                </div>
                                                <div className="timestamp">
                                                    {group.startDate}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}


                {props.commentGroups.length === 0 && (
                    <div className={classes.emptyCommentsListMessage}>
                        {props.labels.emptyCommentsListMessage}
                    </div>
                )}

                <Box textAlign="center">
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={editingComment !== undefined}
                        onClick={props.onReloadCommentsClick}
                        startIcon={<Icon>refresh</Icon>}
                    >
                        {props.labels.reloadCommentsButtonLabel}
                    </Button>
                </Box>

                <form
                    className={clsx(formClasses.root, classes.commentMessageForm)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <div className={clsx(formClasses.formActionContainer)}>
                        <TextField
                            className={classes.commentMessageInput}
                            variant="outlined"
                            fullWidth
                            label={selectedComment && editingComment ? props.labels.editCommentInputLabel : props.labels.addCommentInputLabel}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                ref        : commentMessageInputRef,
                                placeholder: selectedComment && editingComment ? selectedComment.message : props.labels.addCommentPlaceholder,
                            }}
                            helperText={props.labels.addCommentHintText}
                            multiline
                            minRows={5}
                            value={commentMessage}
                            onChange={(event: React.FormEvent) => {
                                setCommentMessage((event.currentTarget as HTMLInputElement).value);
                            }}
                        />
                        <div>
                            {editingComment ? (
                                <Box display="flex" flexDirection="column">
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        color="primary"
                                        variant="contained"
                                        disabled={!commentMessage || commentMessage.length <= 0}
                                        onClick={onFormSubmit}
                                    >
                                        {props.labels.updateButtonLabel}
                                    </Button>
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        onClick={onCancelUpdateCommentClicked}
                                    >
                                        {props.labels.cancelButtonLabel}
                                    </Button>
                                </Box>
                            ) : (
                                <IconButton
                                    className={clsx(formClasses.formActionButton)}
                                    color="primary"
                                    disabled={!commentMessage || commentMessage.length <= 0}
                                    onClick={onFormSubmit}
                                >
                                    <Icon>send</Icon>
                                </IconButton>
                            )}
                        </div>
                    </div>
                </form>
            </div>


            <Menu
                className={classes.optionsMenu}
                getContentAnchorEl={null}
                anchorEl={menuAnchorEl}
                anchorOrigin={{
                    vertical  : 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical  : 'top',
                    horizontal: 'right',
                }}
                keepMounted
                open={isMenuOpened}
                onClose={() => {
                    onMenuClosed();
                    setSelectedComment(undefined);
                }}
            >
                <MenuItem
                    disabled={false}
                    onClick={(event: React.MouseEvent) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onMenuClosed();
                        onEditCommentClicked();
                    }}
                >
                    <ListItemIcon>
                        <Icon className="icon edit-comment">edit</Icon>
                    </ListItemIcon>
                    {props.labels.editCommentInputLabel}
                </MenuItem>
                <MenuItem
                    disabled={false}
                    onClick={(event: React.MouseEvent) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onMenuClosed();
                        onDeleteCommentClicked();
                    }}
                >
                    <ListItemIcon>
                        <Icon className="icon delete-comment">delete_outline</Icon>
                    </ListItemIcon>
                    {props.labels.deleteCommentButtonLabel}
                </MenuItem>
            </Menu>
        </Paper>
    );

};
